import { useState } from 'react';
import Lottie from 'react-lottie';

import { SparklesIcon } from '@heroicons/react/20/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { useWindowSize } from 'usehooks-ts';

import dashboardApi from '@/api/dashboard';
import organisationsAPI from '@/api/organisations';
import SampleQRCode from '@/assets/images/email_signature_qr.png';
import AITemplates from '@/assets/lotties/ai-templates.json';
import { ErrorAlert } from '@/components/Alert';
import Button, { BUTTON_KIND } from '@/components/Button';
import Input from '@/components/Input';
import ModalNoButton from '@/components/Modals/ModalNoButton';
import { ProfilePreview } from '@/components/ProfilePreview';
import { AppleGenericPass } from '@/components/Templates/DigitalWallet';
import { DetailedEmailSignaturePreview } from '@/components/Templates/EmailSignature/Preview';
import {
  sampleProfile,
  sampleProfileUrl,
} from '@/components/Templates/EmailSignature/data';
import COLORS from '@/constants/colors';
import useAuth from '@/hooks/useAuth';
import { useRateLimit } from '@/hooks/useRateLimit';
import {
  IBrandFetchOrganisation,
  IDigitalWalletDesignRequest,
  IOrganisationSettings,
  UpdateEmailSignatureRequest,
} from '@/types/IOrganisation';

function getDomain(website: string) {
  const url = new URL(website.includes('://') ? website : `https://${website}`);
  const domain = url.hostname.replace('www.', '');

  if (domain.includes('.')) {
    return domain;
  }

  return null;
}

export function DesignWithAI() {
  return (
    <div className="flex flex-col md:flex-row justify-between gap-4">
      <div className="flex flex-col text-sm">
        <span className="text-gray-900 font-medium">
          Need our help? Use our custom AI generator.
        </span>
        <span className="text-gray-500">
          We will try to find your brand colours and a suitable cover image.
        </span>
      </div>
      <div className="self-end">
        <DesignWithAIButton />
      </div>
    </div>
  );
}

export function DesignWithAIButton() {
  const { width } = useWindowSize();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        buttonText="Design with AI"
        icon={<SparklesIcon />}
        onClick={() => setIsOpen(true)}
      />
      <DesignWithAIModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
}

type TemplateType = 'profile' | 'digital-wallet' | 'email-signature';

type DesignWithAIModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccessCallback?: (data: IBrandFetchOrganisation) => void;
};

export function DesignWithAIModal({
  isOpen,
  setIsOpen,
}: DesignWithAIModalProps) {
  const { orgID } = useAuth();
  const queryClient = useQueryClient();
  const { canAttempt, useAttempt, attemptsLeft } = useRateLimit();

  const [website, setWebsite] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState<Set<TemplateType>>(
    new Set(['digital-wallet', 'email-signature', 'profile']),
  );

  const {
    mutateAsync: fetchBrand,
    isPending: isLoading,
    data: brandSettings,
    error,
  } = useMutation({
    mutationFn: getBrandSettings,
    onSettled: useAttempt,
  });
  const organisationSettingMutation = useMutation({
    mutationFn: updateOrganisationSettings,
  });
  const digitalWalletMutation = useMutation({
    mutationFn: updateDigitalWalletDesign,
  });
  const emailSignatureMutation = useMutation({
    mutationFn: updateEmailSignature,
  });

  const { data: emailSignature } = useQuery({
    queryKey: ['getEmailSignature', orgID],
    queryFn: getEmailSignature,
    enabled: orgID !== undefined,
    select: data => {
      if (data === undefined) {
        return data;
      }

      const {
        logo_file,
        banner_file,
        text_color,
        background_color,
        primary_text_color,
        ...rest
      } = data;

      return rest;
    },
  });

  async function getBrandSettings() {
    if (orgID === undefined || !canAttempt) {
      return;
    }

    const domain = getDomain(website);

    if (domain === null) {
      const error = new Error('Invalid URL');
      error.name = 'INVALID_URL';
      throw error;
    }

    const res = await dashboardApi.searchBrand(orgID, domain);

    return res.data;
  }

  async function getEmailSignature() {
    if (orgID === undefined) {
      return;
    }

    const { data } = await organisationsAPI.getEmailSignature(orgID);

    return data.data;
  }

  async function updateOrganisationSettings(
    payload: Partial<IOrganisationSettings>,
  ) {
    if (orgID === undefined) {
      return;
    }

    await organisationsAPI.updateOrganisationSettings(orgID, {
      organisation_setting: payload,
    });
  }

  async function updateDigitalWalletDesign(
    payload: IDigitalWalletDesignRequest,
  ) {
    if (orgID === undefined) {
      return;
    }

    await organisationsAPI.updateDigitalWalletDesign(orgID, payload);
  }

  async function updateEmailSignature(signature: UpdateEmailSignatureRequest) {
    if (orgID === undefined) {
      return;
    }

    await organisationsAPI.updateEmailSignature(orgID, signature);
  }

  function toggleTemplate(template: TemplateType) {
    setSelectedTemplates(prev => {
      const next = new Set(prev);
      if (next.has(template)) {
        next.delete(template);
      } else {
        next.add(template);
      }

      return next;
    });
  }

  async function updateTemplates() {
    if (brandSettings === undefined) {
      return;
    }

    if (emailSignature === undefined) {
      return;
    }

    const mutations = {
      profile: () =>
        organisationSettingMutation.mutateAsync({
          bg_color: brandSettings.bg_color,
          text_color: brandSettings.text_color,
          button_bg_color: brandSettings.button_bg_color,
          button_text_color: brandSettings.button_text_color,
          company_logo_file_id: brandSettings.cover_image_file?.id || null,
        }),
      'digital-wallet': () =>
        digitalWalletMutation.mutateAsync({
          digital_wallet_design: {
            text_color: brandSettings.text_color,
            background_color: brandSettings.bg_color,
            logo_file_id: brandSettings.logo_file?.id || null,
            banner_image_file_id: brandSettings.banner_image_file?.id || null,
          },
        }),
      'email-signature': () =>
        emailSignatureMutation.mutateAsync({
          ...emailSignature,
          background_color: brandSettings.bg_color,
          text_color: brandSettings.text_color,
          primary_text_color: COLORS.organisation.defaultProfile.textColor,
          logo_file_id: brandSettings.logo_file?.id || null,
          banner_file_id: brandSettings.banner_image_file?.id || null,
        }),
    };

    const promises = [];

    for (const template of selectedTemplates) {
      const mutation = mutations[template];
      promises.push(mutation());
    }

    await Promise.all(promises);

    queryClient.invalidateQueries({ queryKey: ['getEmailSignature', orgID] });
    queryClient.invalidateQueries({
      queryKey: ['getDigitalWalletDesign', orgID],
    });
    queryClient.invalidateQueries({
      queryKey: ['showOrganisationSettings', orgID],
    });
  }

  const isAnyLoading =
    organisationSettingMutation.isPending ||
    digitalWalletMutation.isPending ||
    emailSignatureMutation.isPending;

  return (
    <ModalNoButton isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="flex p-2 space-x-4">
        <div className="flex flex-col justify-center w-[400px] space-y-6">
          <div className="flex flex-col space-y-2">
            <span className="font-medium text-xl text-gray-900">
              {brandSettings ? 'Select your assets' : 'Brand Assets Finder'}
            </span>
            <span className="text-gray-600 text-sm">
              {brandSettings ? (
                'Choose which assets you would like to use across your Tapt account.'
              ) : (
                <>
                  Enter your company&apos;s website. We will use it to find your
                  colours and logos. <i>(Limited to 3 attempts per 24 hours)</i>
                </>
              )}
            </span>
          </div>
          {error && error.name !== 'INVALID_URL' && (
            <ErrorAlert message="We couldn't find your brand assets. Please try a different domain." />
          )}
          <div className="space-y-2">
            <Input
              type="text"
              label="Company Website"
              value={website}
              onChange={setWebsite}
              placeholder="https://tapt.io/"
              error={error?.name === 'INVALID_URL'}
              disabled={!!brandSettings}
            />
            {attemptsLeft === 1 && (
              <div className="italic text-red-400 text-sm">
                1 more attempt available.
              </div>
            )}
            {!canAttempt && (
              <div className="italic text-red-400 text-sm">
                No more attempts available.
              </div>
            )}
          </div>
          <div className="flex space-x-2 self-end">
            <Button
              buttonText="Cancel"
              kind={BUTTON_KIND.WHITE}
              onClick={() => setIsOpen(false)}
            />
            {brandSettings ? (
              <Button
                buttonText="Confirm"
                onClick={async () => {
                  await updateTemplates();
                  setIsOpen(false);
                }}
                disabled={selectedTemplates.size === 0}
                loading={isAnyLoading}
              />
            ) : (
              <Button
                buttonText="Find Assets"
                onClick={() => fetchBrand()}
                loading={isLoading}
                disabled={!website || !canAttempt}
              />
            )}
          </div>
        </div>
        <div className="w-[525px] h-[525px]">
          {brandSettings ? (
            <div className="flex gap-4 h-full">
              <div
                className={clsx(
                  'h-full flex items-center rounded-md p-6 cursor-pointer border',
                  selectedTemplates.has('profile')
                    ? 'border-brand-500'
                    : 'border-gray-200',
                )}
                onClick={() => toggleTemplate('profile')}
              >
                <motion.div
                  className="select-none"
                  style={{ zoom: 0.7 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ProfilePreview
                    content={sampleProfile}
                    settings={{
                      bgColor: brandSettings.bg_color,
                      textColor: brandSettings.text_color,
                      buttonBgColor: brandSettings.button_bg_color,
                      buttonTextColor: brandSettings.button_text_color,
                      companyLogo: brandSettings.cover_image_file,
                    }}
                  />
                </motion.div>
              </div>
              <div className="flex flex-col w-[46%] gap-4">
                <div
                  className={clsx(
                    'flex items-center justify-center flex-1 rounded-md p-6 border',
                    selectedTemplates.has('digital-wallet')
                      ? 'border-brand-500'
                      : 'border-gray-200',
                  )}
                  onClick={() => toggleTemplate('digital-wallet')}
                >
                  <motion.div
                    className="select-none"
                    style={{ zoom: 0.7 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <AppleGenericPass
                      payload={{
                        backgroundColor: brandSettings.bg_color,
                        textColor: brandSettings.text_color,
                        logo: brandSettings.logo_file?.small_url || null,
                        title: 'Sample Inc',
                        header: 'John Doe',
                      }}
                    />
                  </motion.div>
                </div>
                <div
                  className={clsx(
                    'flex items-center justify-center flex-1 rounded-md p-3 cursor-pointer border',
                    selectedTemplates.has('email-signature')
                      ? 'border-brand-500'
                      : 'border-gray-200',
                  )}
                  onClick={() => toggleTemplate('email-signature')}
                >
                  <motion.div
                    className="p-3 rounded-md shadow-xl select-none [&_a]:pointer-events-none [&_a]:cursor-default"
                    style={{ zoom: 0.4 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <DetailedEmailSignaturePreview
                      signature={{
                        type: 'detailed',
                        show_full_name: true,
                        show_profile_image: true,
                        show_job_title: true,
                        show_email: true,
                        show_company_name: true,
                        show_social_links: true,
                        show_company_website: true,
                        show_add_to_contacts: true,
                        show_banner: true,
                        show_qr_code: true,
                        show_company_address: false,
                        show_company_phone: false,
                        show_mobile_number: false,
                        background_color: brandSettings.bg_color,
                        text_color: brandSettings.text_color,
                        primary_text_color:
                          COLORS.organisation.defaultProfile.textColor,
                        logo_file: brandSettings.logo_file,
                        banner_file: brandSettings.banner_image_file,
                        disclaimer_text: '',
                      }}
                      profile={sampleProfile}
                      assets={{
                        profile_image_url: sampleProfileUrl,
                        qr_code_url: SampleQRCode,
                        banner_image_url:
                          brandSettings.banner_image_file?.original_url || null,
                      }}
                      sampleMode
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          ) : (
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData: AITemplates,
              }}
            />
          )}
        </div>
      </div>
    </ModalNoButton>
  );
}
